import React from 'react';
import {
	Upload,
	UploadOnAddEvent,
	UploadOnProgressEvent,
	UploadOnStatusChangeEvent,
	UploadFileStatus,
	UploadFileInfo,
	UploadListItemProps
} from '@progress/kendo-react-upload';
import classNames from 'classnames';
import styled from 'styled-components';
import FieldDecorator from './FieldDecorator';
import './fieldStyles.less';
import '../commonComponents.less';
import { apiRootUrl } from '../../config.local';
import Loading from '../Loading';
import Blob from '../../models/be/db/Blob';
import { getCompressedFile, getFileExtension, hasValue } from '../../lib/utils';
import AppApi from '../../core/AppApi';
import MessageBox from '../../appComponents/common/MessageBox';

const FileUploadField = (props: Props) => {
	const [uploadErrorMessage, setUploadErrorMessage] = React.useState('');
	const handleOnError = (message: string) => {
		setUploadErrorMessage(message);
	};

	return (
		<FieldDecorator
			label={props.label}
			className={classNames(props.className, 'fileUploadField')}
			errorMessage={props.errorMessage}
		>
			<MessageBox error={uploadErrorMessage} />
			{/* eslint-disable-next-line react/jsx-props-no-spreading */}
			<FileUpload {...props} onError={handleOnError} />
		</FieldDecorator>
	);
};

export const imageAndPdfFileExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];

// For uploads that are rendered as an image rather than linked to as a document, such as a branding logo shown
// in reports, emails and the login page, so a PDF is not usable.
export const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.webp'];

FileUploadField.defaultProps = {
	label: undefined,
	className: undefined,
	allowedExtensions: imageAndPdfFileExtensions,
	errorMessage: undefined,
	disabled: false,
	onError: null,
	showLoading: true,
	onDownloadFile: null,
	isFileUploadRequired: false
};

export default FileUploadField;

const FileUpload = (props: FileUploadProps) => {
	const [loading, setLoading] = React.useState(false);
	const [files, setFiles] = React.useState(createUploadFileInfoArray(props.fileName));
	// Is not case sensitive on the front end, but is in the back end
	const { allowedExtensions } = props;
	const extensionsWithoutDot = allowedExtensions.map((ext) => ext.replace('.', ''));

	React.useEffect(() => {
		setFiles(createUploadFileInfoArray(props.fileName));
	}, [props.fileName]);

	const handleRemoveRequest = async (uploadingFiles: UploadFileInfo[]): Promise<{ uid: string }> => {
		setFiles([]);
		const file = uploadingFiles[0];
		await deleteFile(props.fileId);
		props.onDeleteFile();
		return new Promise((resolve) => resolve({ uid: file.uid }));
	};

	const deleteFile = async (fileId: string) => {
		const xhr = new XMLHttpRequest();
		const token = await AppApi.getOrGenerateAccessToken();
		const queryString = `${props.queryString ? `${props.queryString}&` : '?'}fileId=${fileId}`;
		xhr.open('delete', `${apiRootUrl}/${props.uploadUrl}${queryString}`);
		xhr.setRequestHeader('Authorization', `Bearer ${token}`);
		xhr.send();
	};

	// Attribution: https://github.com/telerik/kendo-react/issues/193
	// https://stackoverflow.com/questions/35711724/upload-progress-indicators-for-fetch
	// https://stackoverflow.com/questions/47638314/a-javascript-upload-progress-display-in-html5
	const handleSaveRequest = async (
		uploadingFiles: UploadFileInfo[],
		options: {
			formData: FormData;
			requestOptions: unknown;
		},
		onProgress: (uid: string, event: ProgressEvent) => void
	) => {
		const file = uploadingFiles[0];
		const { uid } = file;
		const formData = new FormData();
		const compressedFile = await getCompressedFile(file);
		formData.append('file', compressedFile, compressedFile.name);

		const token = await AppApi.getOrGenerateAccessToken();

		const saveRequestPromise: Promise<{ uid: string }> = new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.upload.addEventListener('progress', (event) => {
				if (event.loaded <= event.total) {
					const percent = Math.round((event.loaded / event.total) * 100);
					onProgress(uid, { loaded: percent, total: 100 } as ProgressEvent);
				}
			});
			xhr.addEventListener('loadend', (event) => {
				const xhrCompletedReadyState = 4;
				if (xhr.readyState === xhrCompletedReadyState && xhr.status === 200) {
					onProgress(uid, { loaded: 100, total: 100 } as ProgressEvent);
					resolve({ uid });
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const blob = { ...JSON.parse((event.target as any).response), originalFileName: file.name };
					props.onFileUpload(false, blob);
					props.onError('');
				} else {
					// eslint-disable-next-line prefer-promise-reject-errors
					reject({ uid });
					props.onFileUpload(false, null);
					const message = (event.target as XMLHttpRequest).response;
					props.onError(message);
				}
			});
			xhr.open('post', `${apiRootUrl}/${props.uploadUrl}${props.queryString}`);
			xhr.setRequestHeader('Authorization', `Bearer ${token}`);
			xhr.send(formData);
		});

		return saveRequestPromise;
	};

	const handleAdd = (event: UploadOnAddEvent) => {
		const newValidFiles = event.newState.filter((file) => (file.validationErrors?.length ?? 0) === 0);
		const newLoading = newValidFiles.length > 0;
		setFiles(event.newState);
		setLoading(newLoading);
		if (props.onFileUpload) {
			props.onFileUpload(newLoading, null);
		}
	};

	const handleProgress = (event: UploadOnProgressEvent) => {
		setFiles(event.newState);
	};

	const handleStatusChange = (event: UploadOnStatusChangeEvent) => {
		if (props.onFileUpload) {
			const file = event.affectedFiles[0];
			// This is when a file upload is re-attempted after a network error.
			if (file?.status === UploadFileStatus.Uploading) {
				props.onFileUpload(true, null);
			}
		}
		setFiles(event.newState);
		setLoading(false);
	};

	const handleRemove = () => {
		setFiles([]);
		props.onFileUpload(false, null);
	};

	const customFileDownloadList = (uploadProps: UploadListItemProps) => {
		return (
			<div>
				{uploadProps.files.map((file: UploadFileInfo) => (
					<StyledFileContainer key={file.name}>
						<StyledDownloadFileIcon className={`k-file-group k-icon ${getFileIcon(file.name)}`} />
						<StyledFileDetails>
							<StyledFileName>
								{props.onDownloadFile ? <a onClick={() => props.onDownloadFile()}>{file.name}</a> : <>{file.name}</>}
								<strong className="k-upload-status">
									<button
										type="button"
										className="k-button k-button-icon k-flat k-upload-action"
										onClick={() => {
											handleRemoveRequest(uploadProps.files);
										}}
									>
										<span aria-label="Remove" title="Remove" className="k-icon k-delete k-i-x" />
									</button>
								</strong>
							</StyledFileName>
							{!extensionsWithoutDot.includes(getFileExtension(file.name)) ? (
								<span className="k-file-validation-message k-text-error">
									Please upload only allowed file extenstion.
								</span>
							) : (
								<span className="k-file-validation-message k-text-success">File(s) successfully uploaded.</span>
							)}
						</StyledFileDetails>
					</StyledFileContainer>
				))}
			</div>
		);
	};

	const getFileIcon = (filename: string) => {
		const fileExtension = getFileExtension(filename);
		switch (fileExtension) {
			case 'jpg':
				return 'k-i-file-image';
			case 'pdf':
				return 'k-i-file-pdf';
			default:
				return 'k-i-file';
		}
	};
	const isImageOrPdfExtensions = allowedExtensions === imageAndPdfFileExtensions;
	const isImageExtensions = allowedExtensions === imageFileExtensions;
	const invalidFileExtension = !files.some((file) => extensionsWithoutDot.includes(getFileExtension(file?.name)));
	const noFilesUploaded = files?.length === 0;

	return (
		<>
			<Upload
				batch={false}
				multiple={false}
				withCredentials={false}
				saveUrl={handleSaveRequest}
				removeUrl={handleRemoveRequest}
				files={files}
				restrictions={{ allowedExtensions }}
				onAdd={handleAdd}
				onProgress={handleProgress}
				onStatusChange={handleStatusChange}
				onRemove={handleRemove}
				disabled={props.disabled}
				listItemUI={props.onDownloadFile ? customFileDownloadList : null}
			/>
			{!isImageOrPdfExtensions && !isImageExtensions && invalidFileExtension && (
				<StyledAcceptedFileType>
					*Please upload only {extensionsWithoutDot.join(', ')} file{allowedExtensions.length > 1 ? 's' : ''}.
				</StyledAcceptedFileType>
			)}{' '}
			{isImageOrPdfExtensions && noFilesUploaded && (
				<StyledAcceptedFileType>*Please upload a PDF or an image file</StyledAcceptedFileType>
			)}
			{isImageExtensions && noFilesUploaded && (
				<StyledAcceptedFileType>*Please upload a PNG, JPG or WEBP file</StyledAcceptedFileType>
			)}
			{noFilesUploaded && props.isFileUploadRequired && <StyledRequired>Required.</StyledRequired>}
			{loading && props.showLoading && <Loading loadingPosition="fixed" noSpinner />}
		</>
	);
};

interface Props extends FileUploadProps {
	label?: string;
	className?: string;
	errorMessage?: string;
	showLoading?: boolean;
}

interface FileUploadProps {
	fileName: string;
	fileId: string;
	uploadUrl: string;
	queryString: string;
	allowedExtensions?: string[];
	onFileUpload: (isUploading: boolean, blob: Blob, deleteDocument?: boolean) => void;
	disabled?: boolean;
	onDeleteFile: () => void;
	onDownloadFile?: () => void;
	isFileUploadRequired?: boolean;
	onError?: (message: string) => void;
	showLoading?: boolean;
}

FileUpload.defaultProps = {
	allowedExtensions: [],
	disabled: false,
	onError: null,
	showLoading: true,
	onDownloadFile: null,
	isFileUploadRequired: false
};

const createUploadFileInfoArray = (fileName: string) => {
	return hasValue(fileName) ? [{ uid: '', name: fileName, status: UploadFileStatus.Uploaded, progress: 100 }] : [];
};

export const documentFileExtensions = ['.doc', '.docx', '.pdf'];

const StyledFileContainer = styled.div`
	display: flex;
	align-items: center;
`;

const StyledFileDetails = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	margin-left: 10px;
`;

const StyledFileName = styled.div`
	display: flex;
	align-items: center;
`;

const StyledDownloadFileIcon = styled.span`
	font-size: 32px;
`;

const StyledRequired = styled.div`
	color: red;
`;

const StyledAcceptedFileType = styled.span`
	font-size: 10px;
	color: red;
`;
